// src/screens/s2/BalanceSheetProjection.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Session 2 Screen 11 — Balance Sheet Projection.
// Total assets (stacked area), total liabilities (reducing line), net assets
// (emphasised navy line) from today through freedom age. Assumptions cascade
// from Screen 10 (revenue/margin/overhead) and capital-allocation decisions
// (historic-liability clearance months, business-EF target, DLA route).
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import {
  ComposedChart, Area, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  ResponsiveContainer, ReferenceLine, Legend,
} from 'recharts';
import { ClientData } from '../../types/ClientData';
import { useXeroBalance } from '../../hooks/useXeroBalance';
import { useXeroData } from '../../hooks/useXeroData';
import { effectiveCTRate } from '../../taxConfig';
import { formatCurrency } from '../../utils/formatters';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

export default function BalanceSheetProjection({ data }: Props) {
  const balance = useXeroBalance(data.xeroTenantId);
  const xero = useXeroData(data.xeroTenantId);

  if (balance.loading || xero.loading) {
    return <div className="p-6 max-w-6xl mx-auto text-sm text-gray-500">Loading balance sheet projection…</div>;
  }
  if (!balance.data || !xero.summary) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <h2 className="text-2xl font-bold text-ma-navy mb-4">Balance Sheet Projection</h2>
        <div className="rounded-2xl bg-amber-50 border-2 border-amber-300 p-5 text-sm text-amber-800">
          {balance.error || xero.error || 'Projection requires balance sheet + P&L.'}
        </div>
      </div>
    );
  }

  const b = balance.data!;
  const s = xero.summary!;

  // ── Starting state ──────────────────────────────────────────────────────
  const startAssets =
    b.cash + b.tradeDebtors + b.otherDebtors + b.prepayments + b.stock +
    (b.dlaBalance > 0 ? b.dlaBalance : 0) + b.fixedAssets + b.goodwill;
  const startLiabilitiesCurrent =
    b.vatLiability + b.ctLiability + b.payeLiability + b.staffPensionLiability + b.accruals +
    (b.dlaBalance < 0 ? Math.abs(b.dlaBalance) : 0);
  const startLiabilitiesNonCurrent = b.hirePurchase + b.bounceBackLoan;
  const startLiabilities = startLiabilitiesCurrent + startLiabilitiesNonCurrent;
  const startNetAssets = startAssets - startLiabilities;

  // ── Projection assumptions ──────────────────────────────────────────────
  const clientAge = data.clientAge || 40;
  const freedomAge = data.targetFreedomAge || 55;
  const yearsToFreedom = Math.max(0, freedomAge - clientAge);
  const thisYear = new Date().getFullYear();

  const revenueGrowth = data.s2RevenueGrowthRate ?? 0.05;
  const grossMarginPct = 90;                                // simple hold — match PL screen default spirit
  const overheadGrowth = data.s2OverheadGrowthRate ?? 0.025;
  const annualPension = (data.s2PensionMonthly ?? s.ownerPension / 12) * 12;
  const annualDividends = (data.s2DividendMonthly ?? 0) * 12;

  // Clearance plan — historic current liabilities drop to zero over N months.
  const clearanceMonths = data.s2HistoricCtLiabilityClearanceMonths ?? 12;
  const clearanceYears = Math.max(1, Math.ceil(clearanceMonths / 12));

  // Non-current debts amortize linearly over 6 years (broad approximation for
  // HP + bounce back loan). Adviser can fine-tune in future.
  const nonCurrentYears = 6;

  // ── Build year-by-year projection ──────────────────────────────────────
  interface Row {
    year: number; assets: number; liabilities: number; netAssets: number;
    retainedEarnings: number;
  }
  const rows: Row[] = [];

  let assets = startAssets;
  let liabilitiesCurrent = startLiabilitiesCurrent;
  let liabilitiesNonCurrent = startLiabilitiesNonCurrent;
  let re = b.retainedEarningsClosing;
  let projRev = s.revenue;
  let projOverheads = s.totalOperating + s.staffCosts + s.ownerSalary;

  // Starting row (year 0)
  rows.push({
    year: thisYear,
    assets: Math.round(startAssets),
    liabilities: Math.round(startLiabilities),
    netAssets: Math.round(startNetAssets),
    retainedEarnings: Math.round(re),
  });

  for (let i = 1; i <= yearsToFreedom; i++) {
    // Grow business P&L to derive retained earnings increment.
    projRev = projRev * (1 + revenueGrowth);
    projOverheads = projOverheads * (1 + overheadGrowth);
    const gp = projRev * (grossMarginPct / 100);
    const ebitda = gp - projOverheads - annualPension;
    const pbt = ebitda - s.financeAndDepreciation;
    const ct = pbt * effectiveCTRate(pbt);
    const pat = pbt - ct;
    const retainedThisYear = pat - annualDividends;

    // Retained earnings add to cash (assets) — strong simplification.
    assets += Math.max(0, retainedThisYear);
    re += retainedThisYear;

    // Liabilities decay.
    liabilitiesCurrent = Math.max(0, startLiabilitiesCurrent - (startLiabilitiesCurrent / clearanceYears) * i);
    liabilitiesNonCurrent = Math.max(0, startLiabilitiesNonCurrent - (startLiabilitiesNonCurrent / nonCurrentYears) * i);
    // Add back this year's new CT exposure so the picture isn't artificially clean.
    liabilitiesCurrent += ct / 4;  // quarterly-ish carrying balance

    const liabilities = liabilitiesCurrent + liabilitiesNonCurrent;
    rows.push({
      year: thisYear + i,
      assets: Math.round(assets),
      liabilities: Math.round(liabilities),
      netAssets: Math.round(assets - liabilities),
      retainedEarnings: Math.round(re),
    });
  }

  // ── Headline dates ─────────────────────────────────────────────────────
  const debtFreeYear = rows.find((r) => r.liabilities <= 100 && r.year > thisYear)?.year;
  const efTargetMonths = data.s2BusinessEfTarget ?? 3;
  const monthlyStaff = s.staffCosts / 12;
  const monthlyOperating = s.totalOperating / 12;
  const monthlyOwnerSalary = s.ownerSalary / 12;
  const fixedCostsMonthly = monthlyStaff + monthlyOperating + monthlyOwnerSalary;
  const efTarget = fixedCostsMonthly * efTargetMonths;
  const efCurrent = data.businessOperatingBufferBalance || 0;
  const efGap = Math.max(0, efTarget - efCurrent);
  // Monthly build approximation — whatever's left after pension + dividend.
  const patMonthly = (s.netProfit * (1 - effectiveCTRate(s.netProfit))) / 12;
  const efMonthlyBuild = Math.max(0, patMonthly - (data.s2DividendMonthly ?? 0) - (data.s2PensionMonthly ?? 0));
  const efMonthsToComplete = efMonthlyBuild > 0 && efGap > 0 ? Math.ceil(efGap / efMonthlyBuild) : 0;
  const efDate = new Date();
  efDate.setMonth(efDate.getMonth() + efMonthsToComplete);
  const efDateStr = efGap === 0 ? 'Already met' : efMonthsToComplete > 0 ? efDate.toISOString().slice(0, 10) : '—';

  const finalRow = rows[rows.length - 1];

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-ma-navy">
          Balance Sheet Projection
          {balance.tenantName && <span className="ml-2 text-sm font-normal text-gray-500">— {balance.tenantName}</span>}
        </h2>
        <p className="text-gray-500 mt-1 text-sm">
          Net assets to age {freedomAge} — retained earnings accumulate as P&amp;L
          levers and capital-allocation decisions play out.
        </p>
      </div>

      {/* PROJECTION CHART */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5 mb-6">
        <div style={{ width: '100%', height: 360 }}>
          <ResponsiveContainer>
            <ComposedChart data={rows} margin={{ top: 20, right: 20, bottom: 8, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="year" tick={{ fontSize: 10 }} stroke="#9CA3AF" />
              <YAxis tick={{ fontSize: 10 }} stroke="#9CA3AF" tickFormatter={(v) => `£${Math.round(v / 1000)}k`} />
              <Tooltip formatter={(v: any) => formatCurrency(Number(v))} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <ReferenceLine x={thisYear} stroke="#9CA3AF" strokeDasharray="4 2" label={{ value: 'Today', fontSize: 10, fill: '#6B7280', position: 'top' }} />
              {/* Recharts 3.x: Line strokes can fail to render inside a
                  ComposedChart when the animation state never resolves —
                  dots appear but the connecting path is invisible. Same
                  workaround as Brief 79's Pie fix: disable animation on
                  every series in the composed chart. */}
              <Area
                type="monotone"
                dataKey="assets"
                name="Total assets"
                stroke="#10B981"
                fill="#10B981"
                fillOpacity={0.3}
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="liabilities"
                name="Total liabilities"
                stroke="#DC2626"
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="netAssets"
                name="Net assets"
                stroke="#1E3A5F"
                strokeWidth={3}
                dot={{ r: 3, fill: '#1E3A5F' }}
                isAnimationActive={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <p className="text-xs text-gray-500 mt-3">
          Green area = total assets. Red line = total liabilities (reducing
          as historic CT/VAT/PAYE clear and long-term debt amortizes). Navy
          line = net assets.
        </p>
      </div>

      {/* HEADLINE CARDS */}
      <div className="grid grid-cols-4 gap-3">
        <div className="rounded-2xl border-2 p-4" style={{ borderColor: '#1E3A5F', backgroundColor: '#F0F4FA' }}>
          <div className="text-xs font-semibold uppercase tracking-wide text-ma-navy mb-1">Net assets at freedom</div>
          <div className="text-xl font-bold text-ma-navy">{finalRow ? formatCurrency(finalRow.netAssets) : '—'}</div>
          <div className="text-xs text-gray-500 mt-1">Age {freedomAge}</div>
        </div>
        <div className="rounded-2xl bg-white border border-gray-200 p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Debt free by</div>
          <div className="text-xl font-bold text-ma-navy">{debtFreeYear ?? 'Outside window'}</div>
          <div className="text-xs text-gray-500 mt-1">
            Historic over {clearanceYears} yr + HP/BBL over {nonCurrentYears} yr
          </div>
        </div>
        <div className="rounded-2xl bg-white border border-gray-200 p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Business EF complete</div>
          <div className="text-xl font-bold text-ma-navy">{efDateStr}</div>
          <div className="text-xs text-gray-500 mt-1">{efTargetMonths}mo target · {formatCurrency(efTarget)}</div>
        </div>
        <div className="rounded-2xl border-2 p-4" style={{ borderColor: '#C9A84C', backgroundColor: '#FFF9ED' }}>
          <div className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: '#8B7A3E' }}>Retained earnings at freedom</div>
          <div className="text-xl font-bold" style={{ color: '#C9A84C' }}>{finalRow ? formatCurrency(finalRow.retainedEarnings) : '—'}</div>
          <div className="text-xs mt-1" style={{ color: '#8B7A3E' }}>Available for extraction or growth</div>
        </div>
      </div>
    </div>
  );
}
